/* Head West listing: loads every product of a collection or search (the hw-feed view), then filters, counts, sorts and
   pages the full set. Adapted from AAD's listing (aad-listing.js) for Head West's own data.
   - Sorting uses Shopify's own order for the whole collection: the feed is requested with the chosen sort_by.
   - Size, overall height, finish (where the listing has a finish option), price and stock are checked on the SAME
     variant, so "24 x 36" + "Gold" only matches a listing that sells a gold 24 x 36.
   - Mirror type, shape, style, frame material and finish (for listings without a finish option) come from the product's
     own tags, the same tags the live site's sidebar and collections use.
   - Overall height comes from each variant's own size table ("... H" in Overall Size), prepared by the generator in
     hw-heights.json. Variants without a labelled height are simply not counted in that group.
   - State lives in the address: refresh restores it, Back and Forward step through it. */
(function () {
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
  const esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const PER = 24;

  const TAGS = {
    mtype: { 'Wall Mirrors': 'Wall', 'Bathroom Vanity': 'Vanity', 'Full Length Mirrors': 'Full length', 'Accent': 'Accent', 'Dresser': 'Dresser', 'LED Mirrors': 'LED', 'Medicine Cabinet Mirrors': 'Medicine cabinet', 'Mirror Set': 'Mirror set' },
    /* "Square" is left out: its three tagged products are an arched mirror, a 5-piece set and a squircle. */
    shape: { 'Rectangular': 'Rectangle', 'Circle': 'Round', 'Oval': 'Oval', 'Arch': 'Arched', 'Shape_Novelty': 'Novelty' },
    style: { 'Modern': 'Modern', 'Traditional': 'Traditional', 'Transitional': 'Transitional', 'Contemporary': 'Contemporary' },
    material: { 'Polystyrene': 'Polystyrene', 'Metal': 'Metal', 'Wood': 'Wood', 'Rope': 'Rope', 'Jute': 'Jute', 'Frameless': 'Frameless' },
    color: { 'Black': 'Black', 'Brass': 'Brass', 'Bronze': 'Bronze', 'Brown': 'Brown', 'Brushed Nickel': 'Brushed Nickel', 'Champagne': 'Champagne', 'Cherry': 'Cherry', 'Chrome': 'Chrome', 'Copper': 'Copper', 'Gold': 'Gold', 'Gray': 'Gray', 'Multi-colored': 'Multi-colored', 'Natural Wood': 'Natural Wood', 'Oil-rubbed Bronze': 'Oil-rubbed Bronze', 'Silver': 'Silver', 'White': 'White' },
  };
  const GROUPS = ['mtype', 'shape', 'size', 'height', 'color', 'material', 'style', 'price', 'stock'];
  const LABELS = { mtype: 'Mirror type', shape: 'Shape', size: 'Size (as listed)', height: 'Overall height (frame included)', color: 'Color / finish', material: 'Frame material', style: 'Style', price: 'Price', stock: 'Availability' };
  const HEIGHTS = [['h1', 'Under 16"', h => h < 16], ['h2', '16" to 34.9"', h => h >= 16 && h < 35], ['h3', '35" to 50.9"', h => h >= 35 && h < 51], ['h4', '51" and taller', h => h >= 51]];
  const VARIANT = new Set(['size', 'height', 'price', 'stock']);
  const CHECKS = GROUPS.filter(k => k !== 'price');

  const sizeKey = s => s == null ? null : String(s).toLowerCase().replace(/["”″“']|\s|inches|in\b/g, '').replace(/×/g, 'x');
  const sizeLabel = (k, raw) => { const m = k && k.match(/^([\d.]+)x([\d.]+)$/); return m ? `${m[1]}" x ${m[2]}"` : String(raw).trim(); };
  const colorKey = s => s == null ? null : String(s).trim().toLowerCase();

  async function loadFeed(url) {
    const out = [], seen = new Set();
    let total = null;
    for (let page = 1; page <= 40; page++) {
      const r = await fetch(`${url}&page=${page}`, { credentials: 'same-origin' });
      if (!r.ok) throw new Error('feed ' + r.status);
      const j = JSON.parse(await r.text());
      total = j.total;
      j.p.forEach(p => { if (!seen.has(p.id)) { seen.add(p.id); out.push(p); } });
      if (!j.next) break;
    }
    return { products: out, total };
  }

  function init(root) {
    const grid = $('[data-hw-grid]', root), status = $('[data-hw-count]', root);
    const native = root.parentElement.querySelector('[data-hw-native]');
    const defaultSort = root.dataset.defaultSort || '';
    const labels = {}; /* value key -> label, per group */
    const feeds = {}; /* sort_by -> ordered ids */
    let items = [], heights = {};
    const read = () => {
      const q = new URLSearchParams(location.search);
      const S = { sort: q.get('sort_by') || defaultSort, page: parseInt(q.get('page'), 10) || 1, min: q.get('min') || '', max: q.get('max') || '', f: {} };
      CHECKS.forEach(k => { S.f[k] = q.getAll(k); });
      return S;
    };
    let S = read();

    function fail(message) {
      root.hidden = true;
      if (native) { native.hidden = false; native.classList.add('is-shown'); }
      const note = native && $('[data-hw-native-note]', native);
      if (note) { note.hidden = false; note.textContent = message + ' Showing the standard list.'; }
    }

    /* Per product: product-level values from tags; per variant: size key, height bucket, finish key, price, stock. */
    function prepare(p) {
      const tags = new Set(p.g || []);
      const prod = {};
      ['mtype', 'shape', 'style', 'material'].forEach(k => { prod[k] = Object.keys(TAGS[k]).filter(t => tags.has(t)).map(t => { labels[k] = labels[k] || {}; const key = colorKey(TAGS[k][t]); labels[k][key] = TAGS[k][t]; return key; }); });
      if (!p.co) prod.color = Object.keys(TAGS.color).filter(t => tags.has(t)).map(t => { labels.color = labels.color || {}; const key = colorKey(t); labels.color[key] = labels.color[key] || t; return key; });
      const vs = p.vs.map(v => {
        const sk = sizeKey(v.s);
        if (sk) { labels.size = labels.size || {}; labels.size[sk] = labels.size[sk] || sizeLabel(sk, v.s); }
        const h = heights[v.id];
        const hb = h == null ? null : (HEIGHTS.find(b => b[2](h)) || [null])[0];
        const ck = p.co ? colorKey(v.c) : null;
        if (ck) { labels.color = labels.color || {}; labels.color[ck] = labels.color[ck] || String(v.c).trim(); }
        return { a: v.a, n: v.n, size: sk, height: hb, color: ck };
      });
      return { ...p, prod, vars: vs };
    }
    HEIGHTS.forEach(b => { labels.height = labels.height || {}; labels.height[b[0]] = b[1]; });
    labels.stock = { in: 'In stock' };

    /* f: the selections to test (a group can be swapped out to count one value). */
    function passes(p, f, min, max) {
      for (const k of ['mtype', 'shape', 'style', 'material']) if (f[k].length && !f[k].some(x => p.prod[k].includes(x))) return false;
      if (!p.co && f.color.length && !f.color.some(x => (p.prod.color || []).includes(x))) return false;
      return p.vars.some(v =>
        (!f.size.length || f.size.includes(v.size)) &&
        (!f.height.length || f.height.includes(v.height)) &&
        (!p.co || !f.color.length || f.color.includes(v.color)) &&
        (min === '' || v.n / 100 >= parseFloat(min)) && (max === '' || v.n / 100 <= parseFloat(max)) &&
        (!f.stock.length || v.a));
    }
    const valuesOf = (p, k) => {
      if (k === 'stock') return p.vars.some(v => v.a) ? ['in'] : [];
      if (['mtype', 'shape', 'style', 'material'].includes(k)) return p.prod[k];
      if (k === 'color' && !p.co) return p.prod.color || [];
      return Array.from(new Set(p.vars.map(v => v[k]).filter(Boolean)));
    };
    function counts(k) {
      const out = {};
      const cand = new Set();
      items.forEach(p => valuesOf(p, k).forEach(x => cand.add(x)));
      cand.forEach(x => {
        const f = { ...S.f, [k]: [x] };
        out[x] = items.filter(p => passes(p, f, S.min, S.max)).length;
      });
      return out;
    }
    const sortVals = (k, c) => (a, b) => {
      if (k === 'height') return a.localeCompare(b);
      if (k === 'size') { const n = s => (s.match(/[\d.]+/g) || [0]).map(Number); const x = n(a), y = n(b); return (x[0] - y[0]) || ((x[1] || 0) - (y[1] || 0)) || a.localeCompare(b); }
      return (c[b] || 0) - (c[a] || 0) || (labels[k][a] || a).localeCompare(labels[k][b] || b);
    };
    function checkGroup(k, prefix) {
      const c = counts(k);
      const vals = Array.from(new Set(Object.keys(c).filter(x => c[x] > 0).concat(S.f[k]))).sort(sortVals(k, c));
      if (!vals.length) return '';
      return `<details class="aad-fgroup" open data-group="${k}"><summary>${LABELS[k]}</summary>${vals.map(v => {
        const on = S.f[k].includes(v);
        return `<label class="aad-fopt"><input type="checkbox" data-k="${k}" value="${esc(v)}"${on ? ' checked' : ''}><span>${esc((labels[k] || {})[v] || v)}</span><span class="aad-fopt__ct">${c[v] || 0}</span></label>`;
      }).join('')}</details>`;
    }
    function priceGroup(prefix) {
      const top = Math.ceil(Math.max(0, ...items.map(p => Math.max(...p.vars.map(v => v.n)))) / 100);
      return `<details class="aad-fgroup" open data-group="price"><summary>Price</summary><div class="aad-fprice">
        <label class="aad-visually-hidden" for="${prefix}Min">Minimum price</label><input id="${prefix}Min" type="number" min="0" inputmode="decimal" data-price="min" placeholder="$0" value="${esc(S.min)}">
        <span aria-hidden="true">to</span>
        <label class="aad-visually-hidden" for="${prefix}Max">Maximum price</label><input id="${prefix}Max" type="number" min="0" inputmode="decimal" data-price="max" placeholder="$${top}" value="${esc(S.max)}">
      </div></details>`;
    }
    function renderFilters() {
      [[$('[data-hw-filters]', root), 'HwLd'], [$('[data-hw-filters-m]', root), 'HwLm']].forEach(([el, prefix]) => {
        if (!el) return;
        const closed = {};
        $$('details', el).forEach(d => { closed[d.dataset.group] = !d.open; });
        el.innerHTML = GROUPS.map(k => k === 'price' ? priceGroup(prefix) : checkGroup(k, prefix)).join('');
        $$('details', el).forEach(d => { if (closed[d.dataset.group]) d.open = false; });
      });
    }
    const card = p => `<li><article class="aad-card">
        <a class="aad-card__tile" href="/products/${esc(p.h)}" aria-label="${esc(p.t)}" tabindex="-1">${p.a ? (p.sl ? '<span class="aad-badge aad-badge--sale">Sale</span>' : '') : '<span class="aad-badge">Sold out</span>'}
          ${p.i ? `<img src="${esc(p.i)}" alt="${esc(p.t)}" loading="lazy" width="540" height="540">` : ''}${p.j ? `<img class="aad-card__alt" src="${esc(p.j)}" alt="" loading="lazy" width="540" height="540">` : ''}
        </a>
        <div class="aad-card__meta">
          <a class="aad-card__ttl" href="/products/${esc(p.h)}" title="${esc(p.t)}">${esc(p.t)}</a>
          ${p.f ? `<span class="aad-card__fact">${esc(p.f)}</span>` : ''}
          <span class="aad-price${p.sl ? ' aad-price--on' : ''}">${p.v ? 'From ' : ''}${esc(p.m)}${p.cm ? `<s>${esc(p.cm)}</s><span class="hw-save">Save ${p.sv}%</span>` : ''}</span>
        </div></article></li>`;
    function write(push) {
      const was = new URLSearchParams(location.search), u = new URLSearchParams();
      ['q', 'type', 'options[prefix]', 'view', 'preview_theme_id'].forEach(k => { if (was.get(k) && !CHECKS.includes(k)) u.set(k, was.get(k)); });
      CHECKS.forEach(k => S.f[k].forEach(v => u.append(k, v)));
      if (S.min !== '') u.set('min', S.min);
      if (S.max !== '') u.set('max', S.max);
      if (S.sort && S.sort !== defaultSort) u.set('sort_by', S.sort);
      if (S.page > 1) u.set('page', S.page);
      const s = u.toString(), url = location.pathname + (s ? '?' + s : '') + location.hash;
      if (url === location.pathname + location.search + location.hash) return;
      if (push) history.pushState({ hw: 1 }, '', url); else history.replaceState({ hw: 1 }, '', url);
    }
    function render(push) {
      const order = feeds[S.sort] || feeds[''];
      const byId = new Map(items.map(p => [p.id, p]));
      const list = order.map(id => byId.get(id)).filter(p => p && passes(p, S.f, S.min, S.max));
      const pages = Math.max(1, Math.ceil(list.length / PER));
      if (S.page > pages) S.page = pages;
      grid.innerHTML = list.slice((S.page - 1) * PER, S.page * PER).map(card).join('');
      $('[data-hw-skeleton]', root).hidden = true;
      $('[data-hw-empty]', root).hidden = list.length > 0;
      const nSel = CHECKS.reduce((n, k) => n + S.f[k].length, 0) + (S.min !== '' || S.max !== '' ? 1 : 0);
      status.textContent = nSel ? `${list.length} of ${items.length} products` : `${items.length} products`;
      renderFilters();
      const chips = [];
      CHECKS.forEach(k => S.f[k].forEach(v => chips.push(`<button type="button" class="aad-chip" data-chip-k="${k}" data-chip-v="${esc(v)}" aria-label="Remove filter ${esc(LABELS[k])}: ${esc((labels[k] || {})[v] || v)}">${esc((labels[k] || {})[v] || v)} <span aria-hidden="true">&times;</span></button>`)));
      if (S.min !== '' || S.max !== '') chips.push(`<button type="button" class="aad-chip" data-chip-price aria-label="Remove price filter">${S.min !== '' ? '$' + esc(S.min) : '$0'} to ${S.max !== '' ? '$' + esc(S.max) : 'any'} <span aria-hidden="true">&times;</span></button>`);
      const cw = $('[data-hw-chips]', root);
      cw.hidden = !chips.length;
      cw.innerHTML = chips.join('') + (chips.length ? '<button type="button" class="aad-tlink" data-hw-clear style="background:none;border:0;padding:0">Clear all</button>' : '');
      const fb = $('[data-hw-fbtn]', root); if (fb) fb.textContent = nSel ? `Filters (${nSel})` : 'Filters';
      const nums = [];
      for (let i = 1; i <= pages; i++) if (i === 1 || i === pages || Math.abs(i - S.page) <= 1) nums.push(i); else if (nums[nums.length - 1] !== 0) nums.push(0);
      $('[data-hw-pager]', root).innerHTML = pages < 2 ? '' :
        (S.page > 1 ? `<button type="button" data-page="${S.page - 1}" aria-label="Previous page">&lsaquo;</button>` : '') +
        nums.map(n => n === 0 ? '<span class="is-gap">&hellip;</span>' : n === S.page ? `<span class="is-current" aria-current="page">${n}</span>` : `<button type="button" data-page="${n}" aria-label="Page ${n}">${n}</button>`).join('') +
        (S.page < pages ? `<button type="button" data-page="${S.page + 1}" aria-label="Next page">&rsaquo;</button>` : '');
      $$('[data-hw-sort]', root).forEach(x => { x.value = S.sort; });
      write(push);
    }
    const toTop = () => window.scrollTo({ top: root.getBoundingClientRect().top + scrollY - 140 });
    /* Shopify sorts the whole collection; each order is read once, then kept. */
    async function ensureSort(sort) {
      if (feeds[sort]) return true;
      status.textContent = 'Sorting products';
      try {
        const { products } = await loadFeed(root.dataset.feed + (sort ? '&sort_by=' + encodeURIComponent(sort) : ''));
        feeds[sort] = products.map(p => p.id);
        return true;
      } catch (e) { return false; }
    }
    async function setSort(sort) {
      if (!(await ensureSort(sort))) { status.textContent += '. That order could not be loaded.'; sort = defaultSort; }
      S.sort = sort; S.page = 1; render(true);
    }

    root.addEventListener('change', e => {
      const t = e.target;
      if (t.matches('input[type=checkbox][data-k]')) {
        const a = S.f[t.dataset.k];
        if (t.checked) { if (!a.includes(t.value)) a.push(t.value); } else a.splice(a.indexOf(t.value), 1);
        S.page = 1; render(true);
      }
      if (t.matches('[data-price]')) {
        const v = t.value.trim();
        S[t.dataset.price] = v !== '' && parseFloat(v) >= 0 ? String(parseFloat(v)) : '';
        S.page = 1; render(true);
      }
      if (t.matches('[data-hw-sort]')) setSort(t.value);
    });
    const clearAll = () => { CHECKS.forEach(k => { S.f[k] = []; }); S.min = ''; S.max = ''; S.page = 1; render(true); };
    root.addEventListener('click', e => {
      const b = e.target.closest('[data-page]');
      if (b) { S.page = +b.dataset.page; render(true); toTop(); return; }
      const c = e.target.closest('[data-chip-k]');
      if (c) { const a = S.f[c.dataset.chipK]; a.splice(a.indexOf(c.dataset.chipV), 1); S.page = 1; render(true); return; }
      if (e.target.closest('[data-chip-price]')) { S.min = ''; S.max = ''; S.page = 1; render(true); return; }
      if (e.target.closest('[data-hw-clear]')) clearAll();
    });
    /* Back and Forward restore the filters, sort and page from the address. */
    addEventListener('popstate', async () => {
      S = read();
      if (!(await ensureSort(S.sort))) S.sort = defaultSort;
      render(false);
    });

    Promise.all([
      loadFeed(root.dataset.feed + (defaultSort ? '&sort_by=' + encodeURIComponent(defaultSort) : '')),
      fetch(root.dataset.heights, { credentials: 'omit' }).then(r => r.ok ? r.json() : {}).catch(() => ({})),
    ]).then(async ([feed, h]) => {
      heights = h || {};
      if (feed.products.length !== feed.total) return fail(`Only ${feed.products.length} of ${feed.total} products loaded.`);
      /* A style view lists products carrying ANY of its tags (a union), each once. */
      const any = (root.dataset.anyTags || '').split(',').map(t => t.trim()).filter(Boolean);
      const base = any.length ? feed.products.filter(p => (p.g || []).some(t => any.includes(t))) : feed.products;
      items = base.map(prepare);
      feeds[defaultSort] = base.map(p => p.id);
      feeds[''] = feeds[defaultSort];
      if (!(await ensureSort(S.sort))) S.sort = defaultSort;
      root.dataset.state = 'ready';
      root.dataset.loaded = items.length;
      if (native) native.hidden = true;
      render(false);
    }).catch(() => fail('The full product list could not be loaded.'));
  }
  function boot() { $$('[data-hw-listing]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
